import React, { useMemo } from 'react';
import { useField } from 'formik';

import ApplePayPaymentMethod from './Methods/ApplePayPaymentMethod';
import PaPaymentMethod from './Methods/PaPaymentMethod';
import CbPaymentMethod from './Methods/CbPaymentMethod';
import PpPaymentMethod from './Methods/PpPaymentMethod';
import QePaymentMethod from './Methods/QePaymentMethod';
import CrPaymentMethod from './Methods/CrPaymentMethod';
import OneyPaymentMethod from './Methods/OneyPaymentMethod';
import { isUk } from 'services/currentCountry';
import PaymentMethod from 'model/PaymentMethod';
import { Cart } from 'types/Cart';

export type SubmitButtonVisibilitiesRecord = Partial<
  Record<keyof typeof PaymentMethod, boolean>
>;

type Props = {
  paymentMethods: string[];
  classes?: { container?: string };
  handleClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  submitClicked: boolean;
  cart: Cart;
  changeSubmitButtonVisibilities: (
    value: (
      previous: SubmitButtonVisibilitiesRecord
    ) => SubmitButtonVisibilitiesRecord
  ) => void;
  setPaymentMethod: (
    paymentMethod?: string,
    catchAbortSignal?: boolean
  ) => Promise<Cart | undefined>;
};

export default function PaymentMethods({
  paymentMethods: paymentMethodList,
  classes,
  handleClick,
  submitClicked,
  changeSubmitButtonVisibilities,
  setPaymentMethod,
  cart,
}: Props): JSX.Element {
  const isUkEnv = isUk();
  const [{ value: currentMethodCode }] = useField<string | null>({
    name: 'paymentMethod',
  });

  const componentMethods = useMemo(
    () => ({
      [PaymentMethod.pa]: PaPaymentMethod,
      [PaymentMethod.cb]: CbPaymentMethod,
      [PaymentMethod.pp]: PpPaymentMethod,
      [PaymentMethod.qe]: QePaymentMethod,
      [PaymentMethod.cr]: CrPaymentMethod,
      [PaymentMethod.applePay]: ApplePayPaymentMethod,
      [PaymentMethod.oney]: OneyPaymentMethod,
    }),
    []
  );

  const paymentMethods = useMemo(() => {
    const data = [...paymentMethodList];

    if (isUkEnv) {
      data.push(PaymentMethod.pa);
    }

    return data;
  }, [paymentMethodList, isUkEnv]);

  return (
    <div className={classes?.container}>
      {paymentMethods.map(methodCode => {
        const Component = componentMethods[methodCode];
        if (Component) {
          return (
            <Component
              key={methodCode}
              submitClicked={submitClicked}
              handleClick={handleClick}
              isSelected={currentMethodCode === methodCode}
              setPaymentMethod={setPaymentMethod}
              cart={cart}
              changeSubmitButtonVisibility={visibility => {
                changeSubmitButtonVisibilities(prev => ({
                  ...prev,
                  [methodCode]: visibility,
                }));
              }}
            />
          );
        }
      })}
    </div>
  );
}
